import { useState, useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';
import styles from './SessionSetup.module.css';

export function ConfigurePreview({ diagnosis, onApply, onCancel, applying }) {
  const {
    orderIssues,
    rosterIssues,
    rotationIssues,
    squadAssignment,
  } = diagnosis;

  // Auto-check categories that have issues
  const [fixOrders, setFixOrders] = useState(orderIssues.detected);
  const [syncRosters, setSyncRosters] = useState(rosterIssues.detected);
  const [rebuildRotations, setRebuildRotations] = useState(false); // Off by default - destructive
  const [autoAssignSquads, setAutoAssignSquads] = useState(false); // Off by default

  // For order override: let user swap the proposed order for dual meets
  const [teamOrder, setTeamOrder] = useState(() => {
    if (!orderIssues.detected || !orderIssues.proposedTeamOrder) return null;
    // Build initial map: teamId → newOrder
    const map = {};
    for (const item of orderIssues.proposedTeamOrder) {
      map[item.teamId] = item.newOrder;
    }
    return map;
  });

  // Compute display order from teamOrder state
  const displayTeamOrder = useMemo(() => {
    if (!orderIssues.detected || !orderIssues.proposedTeamOrder) return [];
    return [...orderIssues.proposedTeamOrder]
      .map((item) => ({
        ...item,
        newOrder: teamOrder?.[item.teamId] ?? item.newOrder,
      }))
      .sort((a, b) => a.newOrder - b.newOrder);
  }, [orderIssues, teamOrder]);

  const isDual = displayTeamOrder.length === 2;

  const handleSwapOrder = () => {
    if (!teamOrder || displayTeamOrder.length < 2) return;
    const newMap = { ...teamOrder };
    // Swap all orders: 0↔1 for dual, or reverse for multi
    const maxOrder = displayTeamOrder.length - 1;
    for (const item of displayTeamOrder) {
      newMap[item.teamId] =
        maxOrder - (teamOrder[item.teamId] ?? item.newOrder);
    }
    setTeamOrder(newMap);
  };

  const handleMoveUp = (teamId) => {
    if (!teamOrder) return;
    const currentOrder = teamOrder[teamId];
    if (currentOrder <= 0) return;
    // Find team currently at position above
    const swapTeamId = Object.keys(teamOrder).find(
      (id) => teamOrder[id] === currentOrder - 1
    );
    if (swapTeamId) {
      setTeamOrder({
        ...teamOrder,
        [teamId]: currentOrder - 1,
        [swapTeamId]: currentOrder,
      });
    }
  };

  const handleMoveDown = (teamId) => {
    if (!teamOrder) return;
    const currentOrder = teamOrder[teamId];
    if (currentOrder >= displayTeamOrder.length - 1) return;
    const swapTeamId = Object.keys(teamOrder).find(
      (id) => teamOrder[id] === currentOrder + 1
    );
    if (swapTeamId) {
      setTeamOrder({
        ...teamOrder,
        [teamId]: currentOrder + 1,
        [swapTeamId]: currentOrder,
      });
    }
  };

  const selectedCount = [
    fixOrders,
    syncRosters,
    rebuildRotations,
    autoAssignSquads,
  ].filter(Boolean).length;

  const handleApply = () => {
    onApply({
      fixOrders,
      syncRosters,
      rebuildRotations,
      autoAssignSquads,
      orderOverride: fixOrders ? teamOrder : null,
    });
  };

  const issueCount =
    (orderIssues.detected ? 1 : 0) +
    (rosterIssues.detected ? 1 : 0) +
    (rotationIssues.detected ? 1 : 0) +
    (squadAssignment.detected ? 1 : 0);

  return (
    <div className={styles.diagPanel}>
      <div className={styles.diagHeader}>
        <span className={styles.diagTitle}>Configuration Preview</span>
        <span className={styles.diagBadge}>
          {issueCount} {issueCount === 1 ? 'issue' : 'issues'} found
        </span>
      </div>

      {/* Order Issues */}
      <div className={styles.diagCategory}>
        <div className={styles.diagCategoryHeader}>
          <Form.Check
            type="checkbox"
            checked={fixOrders}
            onChange={(e) => setFixOrders(e.target.checked)}
            disabled={!orderIssues.detected || applying}
            className={styles.diagCheck}
          />
          <span className={styles.diagCategoryTitle}>
            Fix Team & Lineup Orders
          </span>
          {!orderIssues.detected && (
            <span className={styles.diagNoChanges}>no changes needed</span>
          )}
        </div>

        {orderIssues.detected && (
          <div className={styles.diagDetails}>
            <div className={styles.diagDescription}>
              {orderIssues.teamOrdersCorrupted &&
                'Team orders are not unique. '}
              {orderIssues.lineupOrdersCorrupted &&
                'Lineup orders are not unique. '}
              {orderIssues.ordersMismatch &&
                'Team and lineup orders do not match.'}
            </div>

            <div className={styles.diagOrderPreview}>
              <div className={styles.diagOrderHeader}>
                <span>Proposed Order</span>
                {isDual && (
                  <button
                    className={styles.diagSwapBtn}
                    onClick={handleSwapOrder}
                    disabled={applying}
                    title="Swap team order"
                  >
                    Swap
                  </button>
                )}
              </div>
              {displayTeamOrder.map((item, idx) => (
                <div key={item.teamId} className={styles.diagOrderRow}>
                  {!isDual && (
                    <div className={styles.diagOrderArrows}>
                      <button
                        className={styles.diagArrowBtn}
                        onClick={() => handleMoveUp(item.teamId)}
                        disabled={idx === 0 || applying}
                      >
                        ^
                      </button>
                      <button
                        className={styles.diagArrowBtn}
                        onClick={() => handleMoveDown(item.teamId)}
                        disabled={
                          idx === displayTeamOrder.length - 1 || applying
                        }
                      >
                        v
                      </button>
                    </div>
                  )}
                  <span className={styles.diagOrderNum}>
                    {item.newOrder + 1}.
                  </span>
                  <span className={styles.diagOrderTeam}>
                    <span className={styles.diagTricode}>{item.tricode}</span>
                    {item.name}
                  </span>
                  {item.currentOrder !== item.newOrder && (
                    <span className={styles.diagOrderChange}>
                      was #{item.currentOrder + 1}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Roster Issues */}
      <div className={styles.diagCategory}>
        <div className={styles.diagCategoryHeader}>
          <Form.Check
            type="checkbox"
            checked={syncRosters}
            onChange={(e) => setSyncRosters(e.target.checked)}
            disabled={!rosterIssues.detected || applying}
            className={styles.diagCheck}
          />
          <span className={styles.diagCategoryTitle}>Sync Rosters</span>
          {!rosterIssues.detected && (
            <span className={styles.diagNoChanges}>no changes needed</span>
          )}
        </div>

        {rosterIssues.detected && (
          <div className={styles.diagDetails}>
            {rosterIssues.missingRosters?.length > 0 && (
              <div className={styles.diagDescription}>
                Add rosters:{' '}
                {rosterIssues.missingRosters
                  .map((r) => `${r.tricode} (${r.suggestedRoster})`)
                  .join(', ')}
              </div>
            )}
            {rosterIssues.unlinkedLineups?.length > 0 && (
              <div className={styles.diagDescription}>
                Link lineups:{' '}
                {rosterIssues.unlinkedLineups.map((r) => r.tricode).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rotation Issues */}
      <div className={styles.diagCategory}>
        <div className={styles.diagCategoryHeader}>
          <Form.Check
            type="checkbox"
            checked={rebuildRotations}
            onChange={(e) => setRebuildRotations(e.target.checked)}
            disabled={!rotationIssues.detected || applying}
            className={styles.diagCheck}
          />
          <span className={styles.diagCategoryTitle}>Rebuild Rotations</span>
          {!rotationIssues.detected && (
            <span className={styles.diagNoChanges}>no changes needed</span>
          )}
        </div>

        {rotationIssues.detected && (
          <div className={styles.diagDetails}>
            <div className={styles.diagDescription}>
              {rotationIssues.rotationsToCreate > 0 &&
                `Create ${rotationIssues.rotationsToCreate} rotation(s). `}
              {rotationIssues.rotationsToDelete > 0 &&
                `Delete ${rotationIssues.rotationsToDelete} rotation(s). `}
              {rotationIssues.squadsToCreate.length > 0 &&
                `Create squad(s): ${rotationIssues.squadsToCreate.join(
                  ', '
                )}. `}
              {rotationIssues.squadsToDelete.length > 0 &&
                `Delete squad(s): ${rotationIssues.squadsToDelete.join(
                  ', '
                )}. `}
              {rotationIssues.stagesToUpdate > 0 &&
                `${rotationIssues.stagesToUpdate} stage(s) will be updated. `}
              {rotationIssues.stagesToDelete > 0 &&
                `${rotationIssues.stagesToDelete} stage(s) will be deleted. `}
              {rotationIssues.stagesToCreate > 0 &&
                `${rotationIssues.stagesToCreate} stage(s) will be created.`}
            </div>
            {rotationIssues.hasExistingJudgeAssignments && (
              <div className={styles.diagWarning}>
                WARNING: Existing judge assignments will be lost on deleted
                stages. Only enable this if the rotation structure needs to
                change.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Squad Assignment */}
      <div className={styles.diagCategory}>
        <div className={styles.diagCategoryHeader}>
          <Form.Check
            type="checkbox"
            checked={autoAssignSquads}
            onChange={(e) => setAutoAssignSquads(e.target.checked)}
            disabled={!squadAssignment.detected || applying}
            className={styles.diagCheck}
          />
          <span className={styles.diagCategoryTitle}>Auto-Assign Squads</span>
          {!squadAssignment.detected && (
            <span className={styles.diagNoChanges}>no changes needed</span>
          )}
        </div>

        {squadAssignment.detected && (
          <div className={styles.diagDetails}>
            {squadAssignment.assignments
              ?.filter((a) => a.changed)
              .map((a, i) => (
                <div key={i} className={styles.diagDescription}>
                  {a.tricode}: {a.currentSquad || 'none'} (pos{' '}
                  {a.currentPosition}){' -> '}Squad {a.newSquad} (pos{' '}
                  {a.newPosition})
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={styles.diagActions}>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onCancel}
          disabled={applying}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleApply}
          disabled={selectedCount === 0 || applying}
          className={styles.diagApplyBtn}
        >
          {applying ? 'Applying...' : `Apply Selected (${selectedCount})`}
        </Button>
      </div>
    </div>
  );
}
